
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface Achievement {
  id: string;
  title: string;
  icon: string;
}

interface AchievementsProps {
  achievements: Achievement[];
  totalReferrals: number;
  soldReferrals: number;
}

// Define all possible achievements
const ALL_ACHIEVEMENTS = [
  { id: 'first', title: 'First Referral', icon: '🌱', description: 'Made your first referral', requirement: '1 referral' },
  { id: 'five', title: '5 Referrals', icon: '🌿', description: 'Made 5 referrals', requirement: '5 referrals' },
  { id: 'ten', title: '10 Referrals', icon: '🌳', description: 'Made 10 referrals', requirement: '10 referrals' },
  { id: 'twentyfive', title: 'Referral Pro', icon: '🏆', description: 'Made 25 referrals', requirement: '25 referrals' },
  { id: 'fifty', title: 'Referral Master', icon: '👑', description: 'Made 50 referrals', requirement: '50 referrals' },
  { id: 'firstsale', title: 'First Sale', icon: '💰', description: 'First referral converted to sale', requirement: '1 sale' },
  { id: 'fivesales', title: '5 Sales', icon: '💎', description: 'Got 5 sales from referrals', requirement: '5 sales' },
  { id: 'tensales', title: 'Sales Expert', icon: '🚀', description: 'Got 10 sales from referrals', requirement: '10 sales' },
];

const Achievements: React.FC<AchievementsProps> = ({ achievements, totalReferrals, soldReferrals }) => {
  const isAchieved = (id: string) => {
    return achievements.some(a => a.id === id);
  };
  
  return (
    <Card className="glass-panel animate-fadeIn">
      <CardHeader>
        <CardTitle className="text-2xl">Your Achievements</CardTitle>
        <CardDescription>
          You've earned {achievements.length} out of {ALL_ACHIEVEMENTS.length} badges
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <TooltipProvider>
            {ALL_ACHIEVEMENTS.map((achievement) => {
              const achieved = isAchieved(achievement.id);
              
              return (
                <Tooltip key={achievement.id}>
                  <TooltipTrigger asChild>
                    <div 
                      className={`
                        flex flex-col items-center justify-center p-4 rounded-lg 
                        ${achieved 
                          ? 'glass-panel transition-scale' 
                          : 'bg-secondary/30 border border-border/50 opacity-60'
                        }
                      `}
                    >
                      <div 
                        className={`
                          text-4xl mb-2
                          ${achieved ? '' : 'grayscale'}
                        `}
                      >
                        {achievement.icon}
                      </div>
                      <h3 className="font-medium text-center">{achievement.title}</h3>
                      
                      {achieved ? (
                        <Badge variant="outline" className="mt-2 bg-accent/20 text-accent-foreground border-accent/40">
                          Achieved
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="mt-2 bg-secondary text-muted-foreground">
                          Locked
                        </Badge>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="glass-panel border-accent/20">
                    <div className="p-1">
                      <p className="font-medium">{achievement.description}</p>
                      <p className="text-sm text-muted-foreground">Requirement: {achievement.requirement}</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
};

export default Achievements;
